import React from "react"
import { useBoolean } from "@design-system/hooks"
import { useFormControl } from "@design-system/form-control"
import { ChevronDownIcon } from "@design-system/icons"
import { callAllHandlers } from "@design-system/utils"

import { Box, styled, forwardRef, createStyles } from "@design-system/core"

import { SelectProps, SelectFieldProps } from "./"

const styles = createStyles((theme) => ({
  container: {
    position: "relative",
  },
  arrow: {
    pointerEvents: "none",
    position: "absolute",
    right: theme.spaces.spacing16,
    top: theme.spaces.spacing8,
    width: theme.spaces.spacing16,
    height: theme.spaces.spacing16,
  },
}))

const stateStyles = createStyles((theme) => ({
  default: {
    fontSize: theme.fontSizes.large,
    height: theme.spaces.spacing40,
    paddingInlineStart: theme.spaces.spacing16,
    width: "100%",
    borderColor: theme.colors.gray200,
    borderRadius: theme.borderRadii.medium,
    color: theme.colors.black,
    appearance: "none",
  },
  focused: {
    borderColor: theme.colors.blue400,
    boxShadow: `0px 0px 4px ${theme.colors.blue400}`,
  },
  invalid: {
    borderColor: theme.colors.red600,
    boxShadow: `0px 0px 0 1px ${theme.colors.red600}`,
  },
  disabled: {
    backgroundColor: theme.colors.gray050,
    color: theme.colors.gray400,
  },
  readOnly: {
    backgroundColor: theme.colors.gray050,
  },
}))

export const SelectField = forwardRef<SelectFieldProps, "select">(
  (props, ref) => {
    const [isFocused, setFocused] = useBoolean()

    const { children, placeholder, ...rest } = props
    const ownProps = {
      ...useFormControl<HTMLSelectElement>(rest),
      onFocus: callAllHandlers(rest.onFocus, props.onFocus, setFocused.on),
      onBlur: callAllHandlers(rest.onBlur, props.onBlur, setFocused.off),
    }

    return (
      <styled.select
        ref={ref}
        style={[
          stateStyles.default,
          isFocused && stateStyles.focused,
          rest.isInvalid && stateStyles.invalid,
          rest.isDisabled && stateStyles.disabled,
          rest.isReadOnly && stateStyles.readOnly,
        ]}
        {...ownProps}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {children}
      </styled.select>
    )
  },
)

/**
 * React component used to select one item from a list of options.
 */
export const Select = forwardRef<SelectProps, "select">((props, ref) => {
  const { rootProps, placeholder } = props

  return (
    <Box style={styles.container} {...rootProps}>
      <SelectField ref={ref} placeholder={placeholder} {...props}>
        {props.children}
      </SelectField>

      <Box style={styles.arrow}>
        <ChevronDownIcon />
      </Box>
    </Box>
  )
})
